import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { API_BASE_URL } from '../config/api';
import { Plus, Edit, Trash2, Camera, X, Briefcase, Inbox, Settings2, Mail, Star } from 'lucide-react';
import { EngagementManager } from '../components/EngagementManager';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const EMPTY_FORM = {
  name: '', specialization: 'M&E Consultant', photo_url: '', bio: '',
  years_experience: '', qualifications: '', sectors: '', location: '', status: 'active'
};

export default function AdminExperts() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [feeInputs, setFeeInputs] = useState({});
  const [managing, setManaging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('experts');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInput = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchAll();
    axios.get(`${API_BASE_URL}/api/experts/specializations`)
      .then(({ data }) => setSpecializations(data.specializations)).catch(() => {});
  }, [user, authLoading]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ex, en, lg] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/experts/admin/list`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/experts/admin/engagements`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/experts/admin/ledger`, { headers: authHeaders(), withCredentials: true })
      ]);
      setExperts(ex.data.experts);
      setEngagements(en.data.engagements);
      setLedger(lg.data);
    } catch (e) {
      console.error('Error fetching experts:', e);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (ex) => {
    setForm({ ...EMPTY_FORM, ...ex, years_experience: ex.years_experience ?? '' });
    setEditingId(ex.id);
    setShowForm(true);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_BASE_URL}/api/membership-applications/upload-photo`, fd);
      setForm(prev => ({ ...prev, photo_url: data.url }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveExpert = async () => {
    if (!form.name.trim() || !form.bio.trim()) { alert('Name and bio are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, years_experience: form.years_experience === '' ? null : Number(form.years_experience) };
      if (editingId) {
        await axios.put(`${API_BASE_URL}/api/experts/admin/${editingId}`, payload, { headers: authHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API_BASE_URL}/api/experts/admin`, payload, { headers: authHeaders(), withCredentials: true });
      }
      setShowForm(false);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save expert');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpert = async (id) => {
    if (!window.confirm('Remove this expert from the panel?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/experts/admin/${id}`, { headers: authHeaders(), withCredentials: true });
      await fetchAll();
    } catch (e) {
      alert('Failed to delete expert');
    }
  };

  const toggleStatus = async (ex) => {
    try {
      await axios.put(`${API_BASE_URL}/api/experts/admin/${ex.id}`,
        { ...ex, status: ex.status === 'active' ? 'inactive' : 'active' },
        { headers: authHeaders(), withCredentials: true });
      await fetchAll();
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const updateEngagementStatus = async (id, status) => {
    try {
      await axios.post(`${API_BASE_URL}/api/experts/admin/engagements/status`,
        { engagement_id: id, status }, { headers: authHeaders(), withCredentials: true });
      await fetchAll();
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const recordFee = async (engagementId) => {
    const input = feeInputs[engagementId];
    if (!input || !input.assignment_fee) { alert('Enter the assignment fee'); return; }
    try {
      await axios.post(`${API_BASE_URL}/api/experts/admin/engagements/fee`,
        { engagement_id: engagementId, assignment_fee: Number(input.assignment_fee), fee_status: input.fee_status || 'due' },
        { headers: authHeaders(), withCredentials: true });
      setFeeInputs(prev => ({ ...prev, [engagementId]: undefined }));
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to record fee');
    }
  };

  const updateFeeStatus = async (entry, fee_status) => {
    try {
      await axios.post(`${API_BASE_URL}/api/experts/admin/engagements/fee`,
        { engagement_id: entry.id, assignment_fee: entry.assignment_fee, fee_status },
        { headers: authHeaders(), withCredentials: true });
      await fetchAll();
    } catch (e) {
      alert('Failed to update fee status');
    }
  };

  const statusColors = { new: 'bg-blue-100 text-blue-800', contacted: 'bg-yellow-100 text-yellow-800', engaged: 'bg-green-100 text-green-800', closed: 'bg-gray-100 text-gray-600' };
  const feeStatusColors = { due: 'bg-red-100 text-red-800', invoiced: 'bg-yellow-100 text-yellow-800', received: 'bg-green-100 text-green-800' };
  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-experts-title">Expert Panel</h1>
          <p className="text-gray-600">Manage empanelled consultants and engagement requests</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await axios.post(`${API_BASE_URL}/api/experts/admin/digest/send`, {}, { headers: authHeaders(), withCredentials: true });
              alert(data.message);
            } catch (e) { alert(e.response?.data?.detail || 'Failed to send digest'); }
          }} data-testid="send-digest-btn">
            <Mail className="h-4 w-4 mr-2" />Send Business Digest
          </Button>
          <Button onClick={openAdd} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="add-expert-btn">
            <Plus className="h-4 w-4 mr-2" />Add Expert
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg p-1 shadow-sm inline-flex mb-6">
        <button onClick={() => setTab('experts')}
          className={`px-5 py-2 rounded-md text-sm font-medium ${tab === 'experts' ? 'bg-[#1E3A5F] text-white' : 'text-gray-600'}`}
          data-testid="tab-experts">
          Experts ({experts.length})
        </button>
        <button onClick={() => setTab('engagements')}
          className={`px-5 py-2 rounded-md text-sm font-medium ${tab === 'engagements' ? 'bg-[#1E3A5F] text-white' : 'text-gray-600'}`}
          data-testid="tab-engagements">
          Engagement Requests ({engagements.length})
        </button>
        <button onClick={() => setTab('ledger')}
          className={`px-5 py-2 rounded-md text-sm font-medium ${tab === 'ledger' ? 'bg-[#1E3A5F] text-white' : 'text-gray-600'}`}
          data-testid="tab-ledger">
          Fee Ledger ({ledger?.entries?.length || 0})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F] mx-auto"></div></div>
      ) : tab === 'experts' ? (
        experts.length === 0 ? (
          <Card className="p-12 text-center" data-testid="experts-admin-empty">
            <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No experts yet. Add your first empanelled consultant.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {experts.map((ex) => (
              <Card key={ex.id} className="p-5" data-testid={`admin-expert-card-${ex.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {ex.photo_url ? (
                      <img src={ex.photo_url} alt={ex.name} className="h-14 w-14 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-14 w-14 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold flex-shrink-0">
                        {ex.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-bold text-[#1E3A5F]">{ex.name}</p>
                      <p className="text-sm text-gray-500">{ex.specialization}{ex.years_experience ? ` • ${ex.years_experience}+ yrs` : ''}</p>
                      <Badge className={ex.status === 'active' ? 'bg-green-100 text-green-800 mt-1' : 'bg-gray-100 text-gray-600 mt-1'}>
                        {ex.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(ex)} data-testid={`edit-expert-btn-${ex.id}`}><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteExpert(ex.id)} className="text-red-500" data-testid={`delete-expert-btn-${ex.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-3 line-clamp-2">{ex.bio}</p>
                <button onClick={() => toggleStatus(ex)} className="text-xs text-[#1E3A5F] underline mt-2" data-testid={`toggle-expert-status-${ex.id}`}>
                  {ex.status === 'active' ? 'Hide from panel' : 'Show on panel'}
                </button>
              </Card>
            ))}
          </div>
        )
      ) : tab === 'engagements' ? (
        engagements.length === 0 ? (
          <Card className="p-12 text-center" data-testid="engagements-empty">
            <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No engagement requests yet.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {engagements.map((en) => (
              <Card key={en.id} className="p-5" data-testid={`engagement-card-${en.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-bold text-[#1E3A5F]">{en.expert_name} <span className="text-gray-400 font-normal">({en.expert_specialization})</span></p>
                    <p className="text-sm text-gray-600">
                      Requested by {en.member_name || en.member_email}{en.organization_name ? ` — ${en.organization_name}` : ''}
                    </p>
                    <p className="text-xs text-gray-400">{en.member_email}{en.contact_phone ? ` • ${en.contact_phone}` : ''} • {new Date(en.created_at).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setManaging(en)}
                      className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                      data-testid={`manage-engagement-btn-${en.id}`}>
                      <Settings2 className="h-4 w-4 mr-1" />Manage
                    </Button>
                    <select
                      value={en.status}
                      onChange={(e) => updateEngagementStatus(en.id, e.target.value)}
                      className={`text-sm rounded-md px-3 py-1.5 border ${statusColors[en.status]}`}
                      data-testid={`engagement-status-select-${en.id}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="engaged">Engaged</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mt-3 bg-gray-50 rounded-lg p-3">{en.project_brief}</p>
                {en.expected_duration && <p className="text-xs text-gray-500 mt-2">Duration: {en.expected_duration}</p>}

                {en.feedback && (
                  <div className="mt-2 flex items-center gap-2 text-sm" data-testid={`admin-feedback-${en.id}`}>
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-4 w-4 ${s <= en.feedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </span>
                    {en.feedback.comment && <span className="text-gray-600 italic">"{en.feedback.comment}"</span>}
                  </div>
                )}

                {/* Fee Recording */}
                {en.assignment_fee !== undefined ? (
                  <div className="mt-3 flex items-center gap-3 flex-wrap text-sm bg-green-50 border border-green-200 rounded-lg p-3" data-testid={`fee-recorded-${en.id}`}>
                    <span className="font-medium text-gray-700">Fee: <strong className="text-[#1E3A5F]">{inr(en.assignment_fee)}</strong></span>
                    <span className="text-gray-700">CSED 5%: <strong className="text-[#E87722]">{inr(en.csed_fee)}</strong></span>
                    <Badge className={feeStatusColors[en.fee_status]}>{en.fee_status}</Badge>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`fee-form-${en.id}`}>
                    <Input
                      type="number" min="0" placeholder="Assignment fee (₹)"
                      className="w-44 h-9"
                      value={feeInputs[en.id]?.assignment_fee || ''}
                      onChange={(e) => setFeeInputs(prev => ({ ...prev, [en.id]: { ...prev[en.id], assignment_fee: e.target.value } }))}
                      data-testid={`fee-input-${en.id}`}
                    />
                    <select
                      className="border rounded-md h-9 px-2 text-sm bg-white"
                      value={feeInputs[en.id]?.fee_status || 'due'}
                      onChange={(e) => setFeeInputs(prev => ({ ...prev, [en.id]: { ...prev[en.id], fee_status: e.target.value } }))}
                      data-testid={`fee-status-input-${en.id}`}
                    >
                      <option value="due">5% Due</option>
                      <option value="invoiced">Invoiced</option>
                      <option value="received">Received</option>
                    </select>
                    <Button size="sm" variant="outline" onClick={() => recordFee(en.id)}
                      className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
                      data-testid={`record-fee-btn-${en.id}`}>
                      Record Fee
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )
      ) : (
        /* Fee Ledger Tab */
        <div data-testid="fee-ledger-tab">
          {/* Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="p-5">
              <p className="text-sm text-gray-600 mb-1">Assignments with Fees</p>
              <p className="text-2xl font-bold text-[#1E3A5F]" data-testid="ledger-total-assignments">{ledger?.totals?.assignments || 0}</p>
            </Card>
            <Card className="p-5">
              <p className="text-sm text-gray-600 mb-1">Fees Facilitated</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">{inr(ledger?.totals?.total_fees_facilitated)}</p>
            </Card>
            <Card className="p-5">
              <p className="text-sm text-gray-600 mb-1">CSED 5% Received</p>
              <p className="text-2xl font-bold text-green-600" data-testid="ledger-commission-received">{inr(ledger?.totals?.commission_received)}</p>
            </Card>
            <Card className="p-5">
              <p className="text-sm text-gray-600 mb-1">CSED 5% Outstanding</p>
              <p className="text-2xl font-bold text-[#E87722]" data-testid="ledger-commission-outstanding">{inr(ledger?.totals?.commission_outstanding)}</p>
            </Card>
          </div>

          {(!ledger || ledger.entries.length === 0) ? (
            <Card className="p-12 text-center" data-testid="ledger-empty">
              <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No fees recorded yet. Record an assignment fee from the Engagement Requests tab.</p>
            </Card>
          ) : (
            <Card className="p-6 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="py-3 pr-4 font-semibold">Expert</th>
                    <th className="py-3 pr-4 font-semibold">Member / Organisation</th>
                    <th className="py-3 pr-4 font-semibold text-right">Assignment Fee</th>
                    <th className="py-3 pr-4 font-semibold text-right">CSED 5%</th>
                    <th className="py-3 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-gray-50" data-testid={`ledger-row-${e.id}`}>
                      <td className="py-3 pr-4 font-medium text-[#1E3A5F]">{e.expert_name}<br /><span className="text-xs text-gray-400 font-normal">{e.expert_specialization}</span></td>
                      <td className="py-3 pr-4">{e.organization_name || e.member_name || e.member_email}</td>
                      <td className="py-3 pr-4 text-right font-medium">{inr(e.assignment_fee)}</td>
                      <td className="py-3 pr-4 text-right font-bold text-[#E87722]">{inr(e.csed_fee)}</td>
                      <td className="py-3 text-right">
                        <select
                          value={e.fee_status}
                          onChange={(ev) => updateFeeStatus(e, ev.target.value)}
                          className={`text-xs rounded-md px-2 py-1 border ${feeStatusColors[e.fee_status]}`}
                          data-testid={`ledger-status-select-${e.id}`}
                        >
                          <option value="due">Due</option>
                          <option value="invoiced">Invoiced</option>
                          <option value="received">Received</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* Engagement Manager Modal */}
      {managing && (
        <EngagementManager
          engagement={managing}
          onClose={() => setManaging(null)}
          onChanged={async () => {
            const { data } = await axios.get(`${API_BASE_URL}/api/experts/admin/engagements`, { headers: authHeaders(), withCredentials: true });
            setEngagements(data.engagements);
            setManaging(prev => data.engagements.find(e => e.id === prev.id) || prev);
            const lg = await axios.get(`${API_BASE_URL}/api/experts/admin/ledger`, { headers: authHeaders(), withCredentials: true });
            setLedger(lg.data);
          }}
        />
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" data-testid="expert-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#1E3A5F]">{editingId ? 'Edit Expert' : 'Add Expert'}</h3>
              <button onClick={() => setShowForm(false)} data-testid="expert-form-close"><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="space-y-4">
              {/* Photo */}
              <div className="flex items-center gap-4">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Expert" className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center"><Camera className="h-6 w-6 text-gray-400" /></div>
                )}
                <input type="file" accept=".jpg,.jpeg,.png" className="hidden" ref={photoInput}
                  onChange={(e) => handlePhotoUpload(e.target.files[0])} data-testid="expert-photo-input" />
                <Button variant="outline" size="sm" onClick={() => photoInput.current?.click()} disabled={uploadingPhoto} data-testid="expert-photo-btn">
                  <Camera className="h-4 w-4 mr-2" />{uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="expert-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Specialisation *</label>
                  <select value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                    className="w-full border rounded-md p-2 bg-white" data-testid="expert-specialization-select">
                    {specializations.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                  <Input type="number" min="0" value={form.years_experience}
                    onChange={(e) => setForm({ ...form, years_experience: e.target.value })} data-testid="expert-experience-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <Input value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Chennai" data-testid="expert-location-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (private — for CSED notifications)</label>
                  <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="expert@example.com" data-testid="expert-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone (private)</label>
                  <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91..." data-testid="expert-phone-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                <Input value={form.qualifications || ''} onChange={(e) => setForm({ ...form, qualifications: e.target.value })}
                  placeholder="e.g. MSW, PhD (Development Studies)" data-testid="expert-qualifications-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sectors / Domains</label>
                <Input value={form.sectors || ''} onChange={(e) => setForm({ ...form, sectors: e.target.value })}
                  placeholder="e.g. Education, Livelihoods, Health" data-testid="expert-sectors-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile / Bio *</label>
                <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="Professional summary shown to members..." data-testid="expert-bio-input" />
              </div>

              <Button onClick={saveExpert} disabled={saving} className="w-full bg-[#E87722] hover:bg-[#d66a1a]" data-testid="expert-save-btn">
                {saving ? 'Saving...' : editingId ? 'Update Expert' : 'Add to Panel'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}
